import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import RaceWeekendToggle from '@/components/RaceWeekendToggle';
import { AA_IMPACT_LOGO_URL, F1_LOGO_URL } from '@/lib/constants';
import { GitCompareArrows, Globe2, MapPinned, Maximize2 } from 'lucide-react';

export default function HeaderBar({ compareCount, compareReady, onOpenCompare, selectedCountry, countryCount, isEmbed, isPageEmbed }) {
  const openFullDashboard = () => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('embed');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  return (
    <header
      className="sticky top-0 z-50 -mx-4 border-b border-white/10 bg-[rgba(10,25,41,0.82)] px-4 py-3 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
      data-testid={isEmbed ? 'embed-header' : 'aa-impact-header'}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          <img
            src={AA_IMPACT_LOGO_URL}
            alt="AA Impact Inc logo"
            className="h-14 w-14 rounded-2xl border border-white/10 object-cover shadow-[0_10px_24px_rgba(0,0,0,0.28)]"
            data-testid="aa-impact-header-logo"
          />

          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl" data-testid="header-title">AA Impact 2026 Sustainability Championship</h1>
              <Badge className="border border-white/10 bg-white/5 text-[10px] uppercase tracking-[0.18em] text-[#4ECDC4]" data-testid="header-country-badge">
                {selectedCountry}
              </Badge>
              {isEmbed && !isPageEmbed ? (
                <Badge className="border border-white/10 bg-[#3498DB]/15 text-[10px] uppercase tracking-[0.18em] text-[#D8F0FF]">Embed mode</Badge>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-white/55">
              <img src={F1_LOGO_URL} alt="Formula 1 logo" className="h-4 w-auto opacity-90" data-testid="f1-logo" />
              <span className="hidden sm:inline">Completed Grand Prix regions first — compare actual F1 session results with sustainability leaders from the same GP market.</span>
              <span className="sm:hidden">F1 session results vs sustainability leaders by GP market.</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/30" data-testid="a2-intelligence-header-mark">
                <span className="text-[#38bdf8]/70">A2</span> Intelligence
              </span>
              <span className="hidden text-white/10 sm:inline">|</span>
              <p className="hidden text-[10px] text-white/25 sm:block">
                Unofficial dashboard — not endorsed by Formula 1. Official/public marks shown for informational presentation only.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
            <MapPinned className="h-4 w-4 text-[#2A9D8F]" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">Coverage</p>
              <p className="text-sm font-semibold text-white" data-testid="header-country-count">
                {countryCount || 0} GP markets
              </p>
            </div>
          </div>

          <div className="hidden items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 md:flex">
            <Globe2 className="h-4 w-4 text-[#3498DB]" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">Mode</p>
              <p className="text-sm font-semibold text-white">Mission Control</p>
            </div>
          </div>

          {isEmbed && !isPageEmbed ? (
            <Button
              type="button"
              variant="outline"
              onClick={openFullDashboard}
              className="h-11 rounded-2xl border-white/10 bg-white/[0.04] px-4 text-white/80 hover:bg-white/[0.08]"
              data-testid="embed-open-full-dashboard"
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              Open full dashboard
            </Button>
          ) : null}

          {!isEmbed || isPageEmbed ? <RaceWeekendToggle /> : null}

          <Button
            type="button"
            onClick={onOpenCompare}
            disabled={!compareReady}
            data-testid="header-compare-button"
            className="h-11 rounded-2xl border border-[#2A9D8F]/30 bg-[#2A9D8F]/15 px-4 text-[#D8FBF7] hover:bg-[#2A9D8F]/25 disabled:border-white/10 disabled:bg-white/[0.04] disabled:text-white/35"
          >
            <GitCompareArrows className="mr-2 h-4 w-4" />
            Compare selection
            <span className="ml-2 rounded-full bg-[#2A9D8F] px-2 py-0.5 text-[11px] font-semibold text-white">
              {compareCount}
            </span>
          </Button>
        </div>
      </div>
    </header>
  );
}
